import { isDeepLinkTarget, type DeepLinkTarget } from '~/lib/deep-link'
import {
  hydrateGridCandidate,
  type CatalogueCandidate,
} from '~/services/catalogue'
import {
  deriveClassicCandidates,
  type ClassicSource,
} from '~/services/classic-catalogue'
import {
  createHydratedSlots,
  leadingKindCount,
  type HydratedSlots,
  type HydrationGate,
} from './catalogue-grid'

// The Classic tab's grid: the Classic collection on the shared slot machinery
// (GLOSSARY.md "Classic collection") — every tree, then every land skin, each
// kind in default order (ADR 0033) — each slot filling in place as its own
// native lookups settle. The launch deep link's target hydrates ahead of
// every other candidate; the other kind sharing its gid number is never the
// target. The gate is the Shop hydration order's, narrowed to the
// leading kind's run: the Classic reveal surface holds no slot of the other
// kind (GLOSSARY.md "Reveal surface"), so those wait for the reveal and to
// come within reach (GLOSSARY.md "In-view hydration") however early they sit
// in the grid.
export interface ClassicGrid extends HydratedSlots {
  // The launch deep link's candidate, when the collection holds it: its
  // bootstrap-derived art is what lets a land skin's showing slide preload
  // with its slot rather than on dialog open.
  target?: CatalogueCandidate
}

export function createClassicGrid(
  source: ClassicSource,
  options?: { gate?: HydrationGate; target?: DeepLinkTarget },
): ClassicGrid {
  const candidates = deriveClassicCandidates(source)
  const gate = options?.gate
  const target = options?.target
  const isTarget =
    target === undefined
      ? undefined
      : (candidate: CatalogueCandidate) => isDeepLinkTarget(candidate, target)
  const grid = createHydratedSlots(
    candidates,
    (candidate) => hydrateGridCandidate(candidate),
    {
      gate: gate && leadingKindGate(gate, leadingKindCount(candidates)),
      priority: isTarget,
    },
  )
  const targetCandidate = isTarget && candidates.find(isTarget)
  return targetCandidate ? { ...grid, target: targetCandidate } : grid
}

function leadingKindGate(gate: HydrationGate, run: number): HydrationGate {
  return {
    ...gate,
    onSurface: (index) => index < run && gate.onSurface(index),
  }
}
